import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import PropTypes from 'prop-types';
import React from 'react';
import RefreshIcon from '@mui/icons-material/Refresh';
import {Box, Button, Container, Grid, Stack, Typography} from '@mui/material';
import {showReportDialog} from '@sentry/react';
import {useAuth} from '@apps-common/auth-react';
import {useTranslation} from 'react-i18next';

import {ReactComponent as BirdnestSatelliteIcon} from '@explorer/assets/images/birdnest-in-your-antenna.svg';

const PlanetSupportButton = () => {
  const {t} = useTranslation();

  return (
    <Button
      color="primary"
      variant="contained"
      href="https://support.planet.com/hc/en-us/requests/new"
      target="_blank"
      rel="noreferrer noopener"
      endIcon={<OpenInNewIcon fontSize="small" />}
    >
      {t('error.buttonReport')}
    </Button>
  );
};

const SentryReportButton = ({eventId}) => {
  const {t} = useTranslation();
  const auth = useAuth();

  return (
    <Button
      color="primary"
      variant="contained"
      onClick={() =>
        showReportDialog({
          eventId: eventId,
          user: {
            name: auth.user?.name,
            email: auth.user?.email,
          },
        })
      }
    >
      {t('error.buttonReport')}
    </Button>
  );
};

SentryReportButton.propTypes = {
  eventId: PropTypes.string.isRequired,
};

const ErrorPage = ({statusCode, message, ...props}) => {
  const {t} = useTranslation();

  return (
    <Box
      height="100%"
      display="flex"
      flexGrow={1}
      alignItems="center"
      justifyContent="center"
    >
      <Container maxWidth="md">
        <Grid container spacing={4}>
          <Grid
            component={Box}
            item
            xs
            sm={3}
            display={{xs: 'none', sm: 'flex'}}
          >
            <BirdnestSatelliteIcon width="100%" />
          </Grid>
          <Grid item container xs={12} sm>
            <Grid item xs container direction="column" spacing={2}>
              <Grid item xs>
                <Box
                  display="flex"
                  flexDirection="column"
                  width="100%"
                  height="100%"
                  justifyContent="center"
                >
                  {statusCode && (
                    <Typography
                      variant="h1"
                      paragraph
                      align="center"
                      noWrap
                      color="grey.500"
                    >
                      {statusCode}
                    </Typography>
                  )}
                  <Typography variant="h5" paragraph>
                    {t('error.title')}
                  </Typography>

                  <Typography color="textPrimary" gutterBottom>
                    {message || t('error.bodyStartUnknown')}
                    {t('error.bodyStartApology')}
                  </Typography>
                  <Typography gutterBottom>
                    {statusCode === 404
                      ? t('error.bodyEnd404')
                      : t('error.bodyEndRestart')}
                  </Typography>
                </Box>
              </Grid>

              <Grid item>
                <Stack direction="row" spacing={2} justifyContent="flex-end">
                  {props.onRetry && (
                    <Button
                      color="primary"
                      variant="outlined"
                      onClick={props.onRetry}
                      startIcon={<RefreshIcon />}
                    >
                      {props.retryMessage || t('error.buttonRetry')}
                    </Button>
                  )}
                  {props.sentryEventId && (
                    <SentryReportButton
                      eventId={props.sentryEventId}
                      authUser={props.authUser}
                    />
                  )}
                  {!props.sentryEventId && props.showSupportButton && (
                    <PlanetSupportButton />
                  )}
                </Stack>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
};

ErrorPage.propTypes = {
  statusCode: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  message: PropTypes.string,
  sentryEventId: PropTypes.string,
  authUser: PropTypes.object,
  showSupportButton: PropTypes.bool,
  onRetry: PropTypes.func,
  retryMessage: PropTypes.string,
};

ErrorPage.defaultProps = {
  showSupportButton: true,
};

export default ErrorPage;
